// TFR (Temporary Flight Restriction) layer family.
//
// Live FAA feed — one GeoJSON source drives three layers: a red
// crosshatch fill (hatch-tfr pattern), a bright red border line, and a
// symbol label showing the NOTAM key at zoom ≥ 6. Layers are mounted
// VISIBLE by default (no layout.visibility block in the original
// addOverlays.ts inline code — preserved byte-identical here).
//
// Extracted from addOverlays.ts as HI-1 batch 7 round 1 (2026-05-24).
// Pure mechanical move — source spec, paint expressions, layer ids,
// minzoom, and layer order are byte-identical to the pre-extraction form.
//
// applyState reads visibility from useUiStore so a basemap setStyle()
// rebuild restores the user's current toggle state transparently.

import type { Map as MlMap } from 'maplibre-gl';
import { EMPTY_FC } from '../MapView';
import { registerLayerFamily } from '../layerVisibilityRegistry';
import { useLayerVisibilityStore } from '../../stores/layerVisibilityStore';
import { refreshOverlay } from '../overlayRuntime';
import { OVERLAY_BY_ID } from '../overlaySpecs';

export function addTfrLayers(map: MlMap): void {
  if (!map.getSource('tfr')) {
    map.addSource('tfr', { type: 'geojson', data: EMPTY_FC });
    map.addLayer({
      id: 'tfr-fill',
      type: 'fill',
      source: 'tfr',
      paint: {
        'fill-pattern': 'hatch-tfr',
        // 0.5 = readable chart/imagery underneath (Wiley feedback 2026-05-09).
        // The crosshatch pattern + bright red border keep the TFR boundary
        // unmistakable even at this opacity. Was 0.9 — too obscuring.
        'fill-opacity': 0.5,
      },
    });
    map.addLayer({
      id: 'tfr-line',
      type: 'line',
      source: 'tfr',
      paint: {
        'line-color': '#dc2626',
        'line-width': 2.5,
      },
    });
    map.addLayer({
      id: 'tfr-label',
      type: 'symbol',
      source: 'tfr',
      minzoom: 6,
      layout: {
        'text-field': ['concat', 'TFR ', ['get', 'NOTAM_KEY']],
        'text-size': 10,
        'text-anchor': 'center',
        'text-font': ['Noto Sans Regular'],
      },
      paint: {
        'text-color': '#fecaca',
        'text-halo-color': '#7f1d1d',
        'text-halo-width': 1.4,
      },
    });
  }
}

registerLayerFamily({
  id: 'tfr',
  label: 'TFRs',
  sourceId: 'tfr',
  layerIds: ['tfr-fill', 'tfr-line', 'tfr-label'],
  mount: (map) => addTfrLayers(map),
  visibilitySelector: (l) => l.tfr,
  applyState: (map) => {
    if (!map.getLayer('tfr-fill')) return;
    const show = useLayerVisibilityStore.getState().layers.tfr;
    const v = show ? 'visible' : 'none';
    map.setLayoutProperty('tfr-fill', 'visibility', v);
    map.setLayoutProperty('tfr-line', 'visibility', v);
    map.setLayoutProperty('tfr-label', 'visibility', v);
  },
  // TFRs refresh every 5 min via a MapView timer; onToggleOn ensures
  // data is loaded even on cold-boot + bbox refresh keeps it current.
  onToggleOn: (map) => {
    const spec = OVERLAY_BY_ID.get('tfr');
    if (!spec) return;
    return refreshOverlay(map, spec);
  },
});
